import { useState, useCallback } from 'react';
import { SwalAlert } from '@/hooks/useSweetAlert';
import {
  defaultCabinConfig,
  defaultCabinModeration,
  cabinBannedUsers,
  cabinBannedIPs,
  cabinModHistory,
} from '@/mocks/adminSettingsCabina';
import type { CabinAdvancedConfig, CabinModerationConfig } from '@/mocks/adminSettingsCabina';

const cabinaSubTabs = [
  { id: 'basica', label: 'Básica', icon: 'ri-settings-3-line', desc: 'Activación y funciones principales de la cabina' },
  { id: 'moderacion', label: 'Moderación', icon: 'ri-shield-check-line', desc: 'Control de contenido, palabras bloqueadas y reglas automáticas' },
  { id: 'baneos', label: 'Baneos', icon: 'ri-forbid-2-line', desc: 'Usuarios bloqueados, IPs y historial de moderación' },
] as const;

type SubTabId = typeof cabinaSubTabs[number]['id'];
type SaveStatus = 'idle' | 'saving' | 'saved';

export default function SettingsCabinaTab() {
  const [activeTab, setActiveTab] = useState<SubTabId>('basica');
  const [cabinConfig, setCabinConfig] = useState<CabinAdvancedConfig>(defaultCabinConfig);
  const [modConfig, setModConfig] = useState<CabinModerationConfig>(defaultCabinModeration);
  const [bannedUsers] = useState(cabinBannedUsers);
  const [bannedIPs] = useState(cabinBannedIPs);
  const [modHistory] = useState(cabinModHistory);
  const [saveStatus, setSaveStatus] = useState<Record<SubTabId, SaveStatus>>({ basica: 'idle', moderacion: 'idle', baneos: 'idle' });

  const handleSave = useCallback((tab: SubTabId) => {
    setSaveStatus((prev) => ({ ...prev, [tab]: 'saving' }));
    setTimeout(() => {
      setSaveStatus((prev) => ({ ...prev, [tab]: 'saved' }));
      SwalAlert.toast('Configuración guardada correctamente', 'success');
      setTimeout(() => setSaveStatus((prev) => ({ ...prev, [tab]: 'idle' })), 1500);
    }, 600);
  }, []);

  const activeItem = cabinaSubTabs.find((t) => t.id === activeTab);
  const activos = bannedUsers.filter((b) => b.activo).length;

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Cabina', value: cabinConfig.activarCabina ? 'Activada' : 'Desactivada', icon: 'ri-broadcast-line', color: cabinConfig.activarCabina ? 'text-emerald-500' : 'text-foreground-400', bg: cabinConfig.activarCabina ? 'bg-emerald-100' : 'bg-background-200' },
          { label: 'Funciones extra', value: `${[cabinConfig.mensajesVoz, cabinConfig.solicitudes, cabinConfig.concursos].filter(Boolean).length} activas`, icon: 'ri-stack-line', color: 'text-violet-500', bg: 'bg-violet-100' },
          { label: 'Palabras filtradas', value: modConfig.palabrasBloqueadas.length, icon: 'ri-file-text-line', color: 'text-red-500', bg: 'bg-red-100' },
          { label: 'Baneos activos', value: activos, icon: 'ri-forbid-2-line', color: 'text-rose-500', bg: 'bg-rose-100' },
        ].map((s) => (
          <button key={s.label} onClick={() => { if (s.label === 'Palabras filtradas') setActiveTab('moderacion'); else if (s.label === 'Baneos activos') setActiveTab('baneos'); }}
            className="bg-background-50 border border-background-200 rounded-xl p-3.5 text-left cursor-pointer hover:border-secondary-300 transition-all">
            <div className="flex items-center gap-2 mb-1.5"><div className={`w-7 h-7 rounded-lg ${s.bg} flex items-center justify-center`}><i className={`${s.icon} ${s.color} text-sm`}></i></div><span className="text-[10px] text-foreground-400 font-medium">{s.label}</span></div>
            <p className="text-sm font-bold text-foreground-900">{s.value}</p>
          </button>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-background-200 pb-2 overflow-x-auto scrollbar-none">
        {cabinaSubTabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-all ${activeTab === tab.id ? 'bg-red-500 text-background-50' : 'text-foreground-500 hover:bg-background-200 hover:text-foreground-700'}`}>
            <i className={`${tab.icon} text-xs`}></i>{tab.label}
            {saveStatus[tab.id] === 'saved' && <i className="ri-check-line text-emerald-400 text-xs animate-pulse ml-0.5"></i>}
          </button>
        ))}
      </div>

      {/* Tab: Básica */}
      {activeTab === 'basica' && (
        <div className="space-y-5">
          {/* Activación global */}
          <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${cabinConfig.activarCabina ? 'bg-emerald-50/60 border-emerald-200' : 'bg-foreground-100 border-foreground-200'}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${cabinConfig.activarCabina ? 'bg-emerald-100' : 'bg-foreground-200'}`}>
              <i className={`${cabinConfig.activarCabina ? 'ri-broadcast-line text-emerald-600' : 'ri-stop-circle-line text-foreground-500'} text-lg`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${cabinConfig.activarCabina ? 'text-emerald-800' : 'text-foreground-600'}`}>Cabina {cabinConfig.activarCabina ? 'activada' : 'desactivada'}</p>
              <p className="text-[11px] text-foreground-500">{cabinConfig.activarCabina ? 'Los oyentes pueden enviar mensajes.' : 'Los oyentes no pueden enviar mensajes.'}</p>
            </div>
            <button onClick={() => setCabinConfig({ ...cabinConfig, activarCabina: !cabinConfig.activarCabina })}
              className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${cabinConfig.activarCabina ? 'bg-emerald-500' : 'bg-foreground-300'}`}>
              <span className={`absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-background-50 transition-transform ${cabinConfig.activarCabina ? 'translate-x-6' : ''}`}></span>
            </button>
          </div>

          {/* Funciones extra */}
          <div className="bg-background-50 border border-background-200 rounded-2xl p-5 space-y-4">
            <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider">Funciones extra</h4>
            {[
              { field: 'mensajesVoz' as const, label: 'Mensajes de voz', desc: 'Permitir que los oyentes envíen notas de voz a la cabina' },
              { field: 'solicitudes' as const, label: 'Solicitudes', desc: 'Habilitar el envío de solicitudes de canciones' },
              { field: 'concursos' as const, label: 'Concursos', desc: 'Activar dinámicas de concursos en vivo desde la cabina' },
            ].map((item) => (
              <div key={item.field} className="flex items-center justify-between">
                <div><p className="text-foreground-800 text-xs font-semibold">{item.label}</p><p className="text-foreground-400 text-[10px]">{item.desc}</p></div>
                <button onClick={() => setCabinConfig({ ...cabinConfig, [item.field]: !cabinConfig[item.field] })}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${cabinConfig[item.field] ? 'bg-emerald-500' : 'bg-background-300'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-background-50 rounded-full shadow-sm transition-transform ${cabinConfig[item.field] ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
                </button>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button onClick={() => handleSave('basica')} disabled={saveStatus.basica === 'saving'}
              className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saveStatus.basica === 'saving' ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      )}

      {/* Tab: Moderación */}
      {activeTab === 'moderacion' && (
        <div className="space-y-5">
          <div className="bg-background-50 border border-background-200 rounded-2xl p-5 space-y-4">
            <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider">Control de Contenido</h4>
            {[
              { field: 'bloquearSpam' as const, label: 'Bloquear spam', desc: 'Detección automática de mensajes repetitivos' },
              { field: 'bloquearLinks' as const, label: 'Bloquear links', desc: 'Eliminar mensajes que contengan enlaces externos' },
              { field: 'bloquearFlood' as const, label: 'Bloquear flood', desc: 'Limitar el envío masivo de mensajes en corto tiempo' },
              { field: 'bloqueoAutomatico' as const, label: 'Bloqueo automático', desc: 'Suspender usuarios tras múltiples infracciones' },
              { field: 'censurarParcial' as const, label: 'Censura parcial', desc: 'Reemplazar palabras prohibidas con asteriscos en vez de bloquear' },
            ].map((item) => (
              <div key={item.field} className="flex items-center justify-between">
                <div><p className="text-foreground-800 text-xs font-semibold">{item.label}</p><p className="text-foreground-400 text-[10px]">{item.desc}</p></div>
                <button onClick={() => setModConfig({ ...modConfig, [item.field]: !modConfig[item.field] })}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${modConfig[item.field] ? 'bg-red-500' : 'bg-background-300'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-background-50 rounded-full shadow-sm transition-transform ${modConfig[item.field] ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
                </button>
              </div>
            ))}
          </div>

          {modConfig.bloqueoAutomatico && (
            <div className="bg-background-50 border border-background-200 rounded-2xl p-5 space-y-3">
              <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider">Configuración de Bloqueo Automático</h4>
              <div>
                <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Advertencias antes del bloqueo</label>
                <input type="number" value={modConfig.advertenciasPreBloqueo} onChange={(e) => setModConfig({ ...modConfig, advertenciasPreBloqueo: parseInt(e.target.value) || 1 })} min={1} max={10}
                  className="w-24 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors" />
              </div>
              <div>
                <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Tiempo de castigo</label>
                <select value={modConfig.tiempoCastigo} onChange={(e) => setModConfig({ ...modConfig, tiempoCastigo: e.target.value })}
                  className="w-48 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none">
                  {['1 hora', '6 horas', '24 horas', '3 días', '7 días', 'Permanente'].map((t) => (<option key={t} value={t}>{t}</option>))}
                </select>
              </div>
            </div>
          )}

          {/* Palabras bloqueadas */}
          <div className="bg-background-50 border border-background-200 rounded-2xl p-5 space-y-3">
            <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider">Palabras Bloqueadas</h4>
            <div className="flex flex-wrap gap-1.5">
              {modConfig.palabrasBloqueadas.length === 0 && <p className="text-[11px] text-foreground-400 italic">No hay palabras en la lista.</p>}
              {modConfig.palabrasBloqueadas.map((w) => (
                <span key={w} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-50 border border-red-200 text-[10px] font-semibold text-red-700">
                  {w}
                  <button onClick={async () => {
                    const result = await SwalAlert.confirm('¿Eliminar palabra?', `Se eliminará "${w}" de la lista de palabras prohibidas.`);
                    if (result.isConfirmed) setModConfig({ ...modConfig, palabrasBloqueadas: modConfig.palabrasBloqueadas.filter((x) => x !== w) });
                  }} className="w-4 h-4 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-200 transition-colors">
                    <i className="ri-close-line text-[10px]"></i>
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button onClick={() => handleSave('moderacion')} disabled={saveStatus.moderacion === 'saving'}
              className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saveStatus.moderacion === 'saving' ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      )}

      {/* Tab: Baneos */}
      {activeTab === 'baneos' && (
        <div className="space-y-5">
          <div className="space-y-2">
            <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider">Usuarios Bloqueados</h4>
            {bannedUsers.length === 0 && <p className="text-[11px] text-foreground-400 italic">No hay usuarios bloqueados.</p>}
            {bannedUsers.map((user) => (
              <div key={user.id} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0 text-red-500 font-bold text-[10px]">{user.nombre.charAt(0)}</div>
                <div className="flex-1 min-w-0"><p className="text-foreground-800 text-xs font-semibold">{user.nombre}</p><p className="text-foreground-400 text-[10px]">{user.motivo}</p></div>
                <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold ${user.activo ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>{user.activo ? 'Activo' : 'Inactivo'}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider">IPs Bloqueadas</h4>
            {bannedIPs.length === 0 && <p className="text-[11px] text-foreground-400 italic">No hay IPs bloqueadas.</p>}
            {bannedIPs.map((ip) => (
              <div key={ip.id} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200">
                <code className="text-foreground-800 text-xs font-mono">{ip.ip}</code>
                <span className="text-foreground-400 text-[10px]">{ip.motivo}</span>
                <span className={`ml-auto px-2 py-0.5 rounded-md text-[9px] font-bold ${ip.activo ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>{ip.activo ? 'Activo' : 'Inactivo'}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button onClick={() => handleSave('baneos')} disabled={saveStatus.baneos === 'saving'}
              className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saveStatus.baneos === 'saving' ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}